// Table (drawing v2.6, list screen `.tbl` and `.mcards`; reference section
// 7): the configurable table. Header 44, sticky, the raised fill; rows 40 /
// 48 / 56 by density; a sort arrow with aria-sort; a selected row on the
// tint; an empty cell reads "Not set"; money right-aligned in tabular
// figures. On a phone the table becomes cards built from the visible
// columns, and the footer says "Showing 50 of 212" from two different
// queries (standard rule 7).

import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { Checkbox } from "./Checkbox";
import { Button } from "./Button";
import type { Density } from "./ItemRow";
import { cx } from "./cx";

export interface Column<Row> {
  key: string;
  label: ReactNode;
  render: (row: Row) => ReactNode;
  /** name 200, text 140, date 120, money 110, badge 120 (reference section 7). */
  kind?: "name" | "text" | "date" | "money" | "badge";
  sortable?: boolean;
  /** Quiet (meta) text. */
  quiet?: boolean;
}

export interface TableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  rowLabel?: (row: Row) => string;
  density?: Density;
  sort?: { key: string; dir: "asc" | "desc" };
  onSort?: (key: string) => void;
  selected?: Set<string>;
  onSelect?: (key: string, on: boolean) => void;
  onSelectAll?: (on: boolean) => void;
  onOpen?: (row: Row) => void;
  /** "Showing 50 of 212": the shown count is rows.length; the total comes from its own query. */
  total?: number;
  onShowMore?: () => void;
  showMoreLabel?: string;
  /** The phone card's second line. */
  cardLine?: (row: Row) => ReactNode;
  className?: string;
  label?: string;
}

export function Table<Row>({ columns, rows, rowKey, rowLabel, density = "standard", sort, onSort, selected, onSelect, onSelectAll, onOpen, total, onShowMore, showMoreLabel = "Show more", cardLine, className, label }: TableProps<Row>) {
  const selectable = Boolean(onSelect);
  const allOn = selectable && rows.length > 0 && rows.every((r) => selected?.has(rowKey(r)));
  const name = columns.find((c) => c.kind === "name") ?? columns[0];
  const badge = columns.find((c) => c.kind === "badge");
  return (
    <section className={cx("pg-card", className)} aria-label={label}>
      <div className="pg-tblwrap">
        <table className={cx("pg-tbl", density === "compact" && "pg-tbl--compact", density === "comfortable" && "pg-tbl--comfy")}>
          <thead>
            <tr>
              {selectable && (
                <th className="pg-ckc">
                  <Checkbox label="Select all" hideLabel checked={allOn} onChange={(e) => onSelectAll?.(e.target.checked)} />
                </th>
              )}
              {columns.map((c) => {
                const sorted = sort?.key === c.key;
                return (
                  <th key={c.key} className={cx(c.kind === "money" && "pg-r")} aria-sort={sorted ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined} style={c.kind ? { minWidth: { name: 200, text: 140, date: 120, money: 110, badge: 120 }[c.kind] } : undefined}>
                    {c.sortable && onSort ? (
                      <button type="button" className="pg-tbl__sort" onClick={() => onSort(c.key)}>
                        {c.label}
                        {sorted && (sort!.dir === "asc" ? <ArrowUp className="pg-ico pg-ico--s" aria-hidden /> : <ArrowDown className="pg-ico pg-ico--s" aria-hidden />)}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const k = rowKey(r);
              const sel = selected?.has(k);
              return (
                <tr key={k} className={cx(sel && "is-sel")}>
                  {selectable && (
                    <td className="pg-ckc">
                      <Checkbox label={`Select ${rowLabel?.(r) ?? k}`} hideLabel checked={Boolean(sel)} onChange={(e) => onSelect?.(k, e.target.checked)} />
                    </td>
                  )}
                  {columns.map((c) => {
                    const v = c.render(r);
                    return (
                      <td key={c.key} className={cx(c.kind === "name" && "pg-n", c.kind === "money" && "pg-r pg-money", c.quiet && "pg-q")}>
                        {v === null || v === undefined || v === "" ? <span className="pg-q">Not set</span> : v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="pg-mcards">
        {rows.map((r) => {
          const k = rowKey(r);
          const sel = selected?.has(k);
          return (
            <div key={k} className={cx("pg-mcard", sel && "is-sel")} onClick={() => onOpen?.(r)}>
              {selectable ? <Checkbox label={`Select ${rowLabel?.(r) ?? k}`} hideLabel checked={Boolean(sel)} onChange={(e) => onSelect?.(k, e.target.checked)} /> : <span />}
              <div style={{ minWidth: 0 }}>
                <div className="pg-mcard__nm">
                  {name?.render(r)}
                  {badge?.render(r)}
                </div>
                <div className="pg-mcard__sb">{cardLine ? cardLine(r) : columns.filter((c) => c !== name && c !== badge).slice(0, 3).map((c) => c.render(r)).filter(Boolean).map((v, i) => <span key={i}>{i > 0 && " · "}{v}</span>)}</div>
              </div>
              <ChevronRight className="pg-ico" aria-hidden />
            </div>
          );
        })}
      </div>
      {total !== undefined && (
        <div className="pg-tfoot">
          <span>
            Showing {rows.length.toLocaleString("en-US")} of {total.toLocaleString("en-US")}
          </span>
          {onShowMore && rows.length < total && (
            <Button size="sm" onClick={onShowMore}>
              {showMoreLabel}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
